import { TOUJOU_FOCUSABLE_ELEMENTS } from './constants';

/**
 * @fileoverview A utility module for finding focusable elements within a given container.
 * @module getFocusableElements
 */

/**
 * An object that provides various collections of focusable elements within a container.
 * The collections are computed dynamically using getter methods.
 *
 * @param {HTMLElement} [container=document.body] The container element to search within.
 * @param {string} [selector='a, button, input, textarea, select, details, iframe, embed, object, summary, dialog, audio[controls], video[controls], [contenteditable], [tabindex]'] A CSS selector for focusable elements.
 * @returns {{all: HTMLElement[], visibleOnly: HTMLElement[], first: HTMLElement | undefined, last: HTMLElement | undefined}} An object containing arrays and single elements.
 */
// eslint-disable-next-line max-len
export function getFocusableElements(container = document.body, selector = TOUJOU_FOCUSABLE_ELEMENTS) {
  return {
    /**
     * Retrieves all focusable elements in the container, including hidden ones.
     * Elements with `disabled` or `hidden` attributes, or `display: none` style are excluded.
     * @type {HTMLElement[]}
     */
    get all() {
      const elements = Array.from(container.querySelectorAll(selector));
      return elements.filter((el) => !(el.hasAttribute('disabled') || el.hasAttribute('hidden') || window.getComputedStyle(el).display === 'none'));
    },

    /**
     * Retrieves only the visible focusable elements.
     * An element is considered visible if it has a width and height greater than zero.
     * @type {HTMLElement[]}
     */
    get visibleOnly() {
      return this.all.filter((el) => el.offsetWidth > 0 && el.offsetHeight > 0);
    },

    /**
     * Retrieves the first visible focusable element.
     * @type {HTMLElement | undefined}
     */

    get first() {
      return this.visibleOnly[0];
    },

    /**
     * Retrieves the last visible focusable element.
     * @type {HTMLElement | undefined}
     */

    get last() {
      return this.visibleOnly[this.visibleOnly.length - 1];
    },
  };
}
